import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, RefreshCcw, CheckCircle, XCircle, AlertTriangle, 
  Package, Calendar, User, Eye, MessageSquare, FileText, Image,
  Clock, DollarSign, Truck, Shield, Award, TrendingUp, Zap,
  ChevronLeft, ChevronRight, Download, MoreHorizontal
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Returns = () => {
  const navigate = useNavigate();
  const [returnRequests, setReturnRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const fetchReturns = async () => {
    try {
        let token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            token = userInfo.token;
        }

        if (!token) {
            toast.error("Please login to view return requests");
            setLoading(false);
            return navigate('/account');
        }

        const res = await axios.get('/api/returns/seller', {
            headers: { Authorization: `Bearer ${token}` }
        });
        setReturnRequests(res.data.returns);
    } catch (error) {
        console.error('Error fetching return requests:', error);
        toast.error('Failed to load return requests');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchReturns();
  }, []);

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
        let token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            token = userInfo.token;
        }
        await axios.put(`/api/returns/${requestId}/status`, 
            { status: newStatus },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        let message = `Replacement request ${newStatus}`;
        if (newStatus === 'Approved') message = "Replacement request approved! Customer will be notified.";
        if (newStatus === 'Completed') message = "Replacement marked as completed.";
        if (newStatus === 'Rejected') message = "Replacement request rejected.";
        
        toast.success(message);
        fetchReturns();
        setShowDetailModal(false);
    } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to update request status');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Requested': 
        return { 
          bg: '#fed7aa', 
          color: '#f59e0b', 
          label: 'Requested', 
          icon: Clock,
          borderColor: '#f59e0b'
        };
      case 'Approved': 
        return { 
          bg: '#dbeafe', 
          color: '#3b82f6', 
          label: 'Approved', 
          icon: CheckCircle,
          borderColor: '#3b82f6'
        };
      case 'Completed': 
        return { 
          bg: '#d1fae5', 
          color: '#10b981', 
          label: 'Completed', 
          icon: Package,
          borderColor: '#10b981'
        };
      case 'Rejected': 
        return { 
          bg: '#fee2e2', 
          color: '#ef4444', 
          label: 'Rejected', 
          icon: XCircle,
          borderColor: '#ef4444'
        };
      default: 
        return { 
          bg: '#f1f5f9', 
          color: '#64748b', 
          label: status, 
          icon: AlertTriangle,
          borderColor: '#cbd5e1'
        };
    }
  };

  const getStats = () => {
    const total = returnRequests.length;
    const requested = returnRequests.filter(r => r.status === 'Requested').length;
    const approved = returnRequests.filter(r => r.status === 'Approved').length;
    const completed = returnRequests.filter(r => r.status === 'Completed').length;
    const rejected = returnRequests.filter(r => r.status === 'Rejected').length;
    return { total, requested, approved, completed, rejected };
  };

  const stats = getStats();

  const filteredRequests = returnRequests.filter(req => {
    const searchLower = searchTerm.toLowerCase();
    const orderIdMatch = (req.order || '').toString().toLowerCase().includes(searchLower);
    const productMatch = req.product?.name?.toLowerCase().includes(searchLower);
    const customerMatch = req.buyer?.username?.toLowerCase().includes(searchLower);
    const matchSearch = orderIdMatch || productMatch || customerMatch;
    const matchFilter = filterStatus === 'All' || req.status === filterStatus;
    return matchSearch && matchFilter;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card-return">
      <div className="stat-icon-return" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-content-return">
        <h3 className="stat-value-return">{value}</h3>
        <p className="stat-title-return">{title}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container-return">
        <div className="spinner-return"></div>
        <p>Loading exchange requests...</p>
      </div>
    );
  }

  return (
    <div className="returns-page">
      {/* Header */}
      <div className="returns-header">
        <div className="header-left">
          <h1 className="page-title-return">Exchange Requests</h1>
          <p className="page-subtitle-return">Manage customer claims for damaged or incorrect deliveries</p>
        </div>
        <button className="btn-refresh-return" onClick={fetchReturns}>
          <RefreshCcw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="stats-return-grid">
        <StatCard title="Total Requests" value={stats.total} icon={Package} color="#8b5cf6" bgColor="#e9d5ff" />
        <StatCard title="Requested" value={stats.requested} icon={Clock} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Approved" value={stats.approved} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Completed" value={stats.completed} icon={Package} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Rejected" value={stats.rejected} icon={XCircle} color="#ef4444" bgColor="#fee2e2" />
      </div>

      {/* Filters */}
      <div className="filters-return-card">
        <div className="search-wrapper-return">
          <Search size={18} className="search-icon-return" />
          <input 
            type="text" 
            className="search-input-return" 
            placeholder="Search by Order ID, Product or Customer..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="status-filters-return">
          <button 
            className={`status-filter-return ${filterStatus === 'All' ? 'active' : ''}`}
            onClick={() => setFilterStatus('All')}
          >
            All
          </button>
          <button 
            className={`status-filter-return ${filterStatus === 'Requested' ? 'active' : ''}`}
            onClick={() => setFilterStatus('Requested')}
          >
            <Clock size={12} /> Requested
          </button>
          <button 
            className={`status-filter-return ${filterStatus === 'Approved' ? 'active' : ''}`}
            onClick={() => setFilterStatus('Approved')}
          >
            <CheckCircle size={12} /> Approved
          </button>
          <button 
            className={`status-filter-return ${filterStatus === 'Completed' ? 'active' : ''}`}
            onClick={() => setFilterStatus('Completed')}
          >
            <Package size={12} /> Completed
          </button>
          <button 
            className={`status-filter-return ${filterStatus === 'Rejected' ? 'active' : ''}`}
            onClick={() => setFilterStatus('Rejected')}
          >
            <XCircle size={12} /> Rejected
          </button>
        </div>
      </div>

      {/* Returns Grid */}
      {filteredRequests.length === 0 ? (
        <div className="empty-state-return">
          <div className="empty-icon-return">
            <RefreshCcw size={64} />
          </div>
          <h3>No return requests</h3>
          <p>You're all caught up! No customers have reported issues.</p>
        </div>
      ) : (
        <>
          <div className="returns-grid">
            {currentRequests.map((request) => {
              const statusConfig = getStatusConfig(request.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <div key={request._id} className="return-card">
                  <div className="return-card-header">
                    <div className="request-id">
                      <span className="id-label">#{request._id.slice(-8).toUpperCase()}</span>
                      <div className="status-indicator" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </div>
                    </div>
                    <div className="request-date">
                      <Calendar size={12} />
                      {new Date(request.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  
                  <div className="return-card-body">
                    <div className="product-info">
                      <div className="product-image">
                        <img 
                          src={request.product?.image || '/assets/images/thumbs/plant-placeholder.png'} 
                          alt={request.product?.name}
                          onError={(e) => { e.target.src = '/assets/images/thumbs/plant-placeholder.png'; }}
                        />
                      </div>
                      <div className="product-details">
                        <h6 className="product-name">{request.product?.name}</h6>
                        <div className="order-id">Order: {request.order?.toString().slice(-8).toUpperCase()}</div>
                      </div>
                    </div>
                    
                    <div className="customer-info">
                      <User size={14} />
                      <span>{request.buyer?.username}</span>
                    </div>
                    
                    <div className="reason-info">
                      <AlertTriangle size={14} className="reason-icon" />
                      <div>
                        <div className="reason-label">Reason for return</div>
                        <div className="reason-text">{request.reason}</div>
                      </div>
                    </div>
                    
                    {request.description && (
                      <div className="description-info">
                        <MessageSquare size={14} />
                        <div>
                          <div className="description-label">Description</div>
                          <div className="description-text">{request.description}</div>
                        </div>
                      </div>
                    )}
                    
                    {request.image && (
                      <div className="proof-image">
                        <div className="proof-label">
                          <Image size={14} />
                          Proof Image
                        </div>
                        <a href={request.image} target="_blank" rel="noopener noreferrer">
                          <img src={request.image} alt="Proof" />
                        </a>
                      </div>
                    )}
                  </div>
                  
                  <div className="return-card-footer">
                    {request.status === 'Requested' && (
                      <div className="action-buttons-return">
                        <button 
                          className="btn-reject" 
                          onClick={() => updateRequestStatus(request._id, 'Rejected')}
                        >
                          <XCircle size={16} />
                          Reject
                        </button>
                        <button 
                          className="btn-approve" 
                          onClick={() => updateRequestStatus(request._id, 'Approved')}
                        >
                          <CheckCircle size={16} />
                          Approve & Replace
                        </button>
                      </div>
                    )}
                    {request.status === 'Approved' && (
                      <button 
                        className="btn-complete" 
                        onClick={() => updateRequestStatus(request._id, 'Completed')}
                      >
                        <Package size={16} />
                        Mark as Completed
                      </button>
                    )}
                    {(request.status === 'Completed' || request.status === 'Rejected') && (
                      <div className="completed-status">
                        <CheckCircle size={14} />
                        {request.status === 'Completed' ? 'Replacement completed' : 'Request rejected'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-return">
              <button 
                className="page-btn-return" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <div className="page-numbers-return">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`page-number-return ${currentPage === i + 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button 
                className="page-btn-return" 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      <style>{`.returns-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.returns-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title-return {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4px;
}

.page-subtitle-return {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.btn-refresh-return {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh-return:hover {
  border-color: #10b981;
  color: #10b981;
}

.stats-return-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

.stat-card-return {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  transition: all 0.3s ease;
}

.stat-card-return:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

.stat-icon-return {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
}

.stat-value-return {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.stat-title-return {
  font-size: 12px;
  color: #64748b;
  margin: 4px 0 0;
}

.filters-return-card {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.search-wrapper-return {
  position: relative;
  flex: 1;
  max-width: 320px;
}

.search-icon-return {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-input-return {
  width: 100%;
  padding: 10px 16px 10px 42px;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-input-return:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.status-filters-return {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.status-filter-return {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 40px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.status-filter-return.active {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.status-filter-return:hover:not(.active) {
  border-color: #10b981;
  color: #10b981;
}

.returns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.return-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  transition: all 0.3s ease;
}

.return-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.return-card-header {
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e8ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.request-id {
  display: flex;
  align-items: center;
  gap: 12px;
}

.id-label {
  font-weight: 700;
  font-family: monospace;
  font-size: 13px;
  color: #1e293b;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}

.request-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #94a3b8;
}

.return-card-body {
  padding: 20px;
}

.product-info {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.product-image {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f5f9;
  flex-shrink: 0;
}

.product-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.order-id {
  font-size: 11px;
  color: #94a3b8;
  font-family: monospace;
}

.customer-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #475569;
}

.reason-info {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.reason-icon {
  color: #f59e0b;
  flex-shrink: 0;
}

.reason-label {
  font-size: 11px;
  font-weight: 600;
  color: #f59e0b;
  margin-bottom: 4px;
}

.reason-text {
  font-size: 13px;
  color: #475569;
}

.description-info {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.description-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}

.description-text {
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.proof-image {
  margin-top: 12px;
}

.proof-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
}

.proof-image a {
  display: block;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.proof-image img {
  width: 100%;
  max-height: 150px;
  object-fit: cover;
}

.return-card-footer {
  padding: 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #e8ecef;
}

.action-buttons-return {
  display: flex;
  gap: 12px;
}

.btn-approve,
.btn-reject,
.btn-complete {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-approve {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  color: white;
}

.btn-approve:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-reject {
  background: white;
  border: 1px solid #ef4444;
  color: #ef4444;
}

.btn-reject:hover {
  background: #fee2e2;
}

.btn-complete {
  width: 100%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  color: white;
}

.btn-complete:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.completed-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #10b981;
}

.pagination-return {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-btn-return {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 40px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn-return:hover:not(:disabled) {
  border-color: #10b981;
  color: #10b981;
}

.page-btn-return:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers-return {
  display: flex;
  gap: 6px;
}

.page-number-return {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-number-return.active {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.page-number-return:hover:not(.active) {
  border-color: #10b981;
  color: #10b981;
}

.empty-state-return {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 24px;
}

.empty-icon-return {
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.loading-container-return {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
}

.spinner-return {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@media (max-width: 768px) {
  .returns-page {
    padding: 16px;
  }

  .returns-grid {
    grid-template-columns: 1fr;
  }

  .stats-return-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filters-return-card {
    flex-direction: column;
    align-items: stretch;
  }

  .search-wrapper-return {
    max-width: none;
  }

  .action-buttons-return {
    flex-direction: column;
  }
}`}</style>
    </div>
  );
};

export default Returns;